import * as React from 'react';
import { Search01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router';
import { Toaster, toast } from 'sonner';
import { AiChatBubble } from '@/components/features/ai-chat/ai-chat-bubble';
import { AppHeader } from '@/components/shared/app-header';
import { BiometricAuthGate } from '@/components/shared/biometric-auth/BiometricAuthGate';
import { OpexMesImage } from '@/components/shared/opex-mes-image';
import { Skeleton } from '@/components/ui/skeleton';
import { SiteSelector } from '@/contexts/site-context';
import { useAuth } from '@/contexts/auth-context';
import { AppSidebar } from '../components/shared/app-sidebar';
import { MobileBottomNav } from '../components/shared/mobile-bottom-nav';
import { NotificationTray } from '../components/shared/notification-tray';
import { PullToRefresh } from '../components/shared/pull-to-refresh';

class UpdateDepthBoundary extends React.Component<{ children: React.ReactNode }, { key: number; error: Error | null }> {
  override state = { key: 0, error: null };
  retries = 0;

  static getDerivedStateFromError(error: Error) {
    // DOM errors from browser extensions — force remount with fresh nodes
    if (error.name === 'NotFoundError' || error.message?.includes('removeChild')) {
      return { key: Date.now(), error: null };
    }
    // Radix context errors when viewport changes (desktop↔mobile) cause
    // DialogTrigger/AlertDialogTrigger to lose their parent context.
    // Force remount to rebuild component tree cleanly.
    if (error.message?.includes('must be used within')) {
      return { key: Date.now(), error: null };
    }
    // All other errors — show fallback instead of blank screen
    console.error('[UpdateDepthBoundary] Uncaught render error:', error);
    return { key: 0, error };
  }

  override componentDidCatch(error: Error) {
    if (error.name === 'NotFoundError' || error.message?.includes('removeChild')) {
      this.retries++;
    }
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-lg font-medium text-destructive">Something went wrong</p>
          <p className="text-sm text-muted-foreground max-w-md break-all">{this.state.error.message}</p>
          <pre className="text-xs text-muted-foreground max-w-full overflow-auto bg-muted p-3 rounded-md max-h-40">{this.state.error.stack}</pre>
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm"
            onClick={() => this.setState({ error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }
    return <React.Fragment key={this.state.key}>{this.props.children}</React.Fragment>;
  }
}

function AuthenticatedLayout() {
  const { isAuthenticated, isPending, user } = useAuth();

  // Fix aria-hidden focus trap warnings from Radix/Vaul drawers:
  // When a drawer closes, aria-hidden is set on it while a child still holds focus.
  // Blur the active element proactively so React can unmount without warnings.
  React.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
          const target = m.target as HTMLElement;
          if (target.getAttribute('aria-hidden') === 'true') {
            const focused =
              (target.querySelector(':focus') as HTMLElement | null) ??
              (document.activeElement as HTMLElement | null);
            if (focused && target.contains(focused)) focused.blur();
          }
        }
      }
    });
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-hidden'],
    });
    return () => observer.disconnect();
  }, []);

  if (isPending) {
    return (
      <div className="flex h-screen">
        <aside className="hidden md:flex w-14 min-w-14 flex-col items-center py-3 border-r border-border bg-sidebar">
          <div className="w-9 h-9 rounded-lg bg-muted-foreground/10" />
        </aside>
        <div className="flex-1 flex flex-col overflow-hidden">
          <div
            className="md:hidden shrink-0"
            style={{ height: 'var(--safe-area-top-clamped, 0px)' }}
          />
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4 md:hidden">
            <Skeleton className="h-6 w-6" />
            <div className="flex items-center gap-2">
              <img
                src="/favicon/android-chrome-512x512.png"
                alt="OpexMES"
                width={24}
                height={24}
                className="object-contain"
              />
              <Skeleton className="h-4 w-16" />
            </div>
          </header>
          <main className="flex flex-1 flex-col gap-4 p-3 sm:p-4 md:gap-6 md:p-6 overflow-auto">
            <div className="space-y-3">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Skeleton className="h-32 rounded-lg" />
              <Skeleton className="h-32 rounded-lg" />
              <Skeleton className="h-32 rounded-lg" />
              <Skeleton className="h-32 rounded-lg" />
            </div>
            <Skeleton className="h-64 rounded-lg" />
          </main>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to login');
    toast.error('Session expired. Please sign in again.', {
      description: 'Redirecting to login...',
    });
    return <Navigate to="/login" />;
  }

  return (
    <UpdateDepthBoundary>
      <div className="flex h-screen">
        <AppSidebar />
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Status bar spacer for mobile (inside container so drawers don't cause shift) */}
          <div
            className="md:hidden shrink-0 bg-background"
            style={{ height: 'var(--safe-area-top-clamped, 0px)' }}
          />

          {/* Desktop Header */}
          <div className="hidden md:block">
            <AppHeader />
          </div>

          {/* Mobile Header */}
          <header className="flex h-14 shrink-0 flex-col justify-center border-b border-border/50 bg-background/80 backdrop-blur-md px-3 md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <OpexMesImage width={72} height={16} />
                  <span className="text-[9px] leading-none text-muted-foreground/50">
                    v{import.meta.env.VITE_APP_VERSION}
                  </span>
                </div>
                <button
                  className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-accent transition-colors text-muted-foreground"
                  onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
                >
                  <HugeiconsIcon icon={Search01Icon} size={16} />
                </button>
              </div>
              <div className="flex items-center gap-0.5">
                <SiteSelector />
                <NotificationTray />
              </div>
            </div>
          </header>

          {/* Main content */}
          <main
            id="main-content"
            className="flex flex-1 flex-col p-3 sm:p-2 md:p-4 md:gap-6 gap-4 pb-20 overflow-auto md:pb-4 relative pt-0"
          >
            <PullToRefresh>
              <BiometricAuthGate>
                <Outlet />
              </BiometricAuthGate>
            </PullToRefresh>
          </main>

          {/* Progressive blur footer */}
          <div className="pointer-events-none absolute inset-x-4 bottom-0 h-14 hidden md:block">
            <div className="h-full w-full bg-gradient-to-t from-background via-background/80 to-transparent" />
          </div>

          {/* Mobile Bottom Navigation */}
          <MobileBottomNav />
          {/* AI Chat Assistant */}
          <AiChatBubble />
          <Toaster />
        </div>
      </div>
    </UpdateDepthBoundary>
  );
}

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
});
